import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { IconMicrosoft } from '../components/icons';
import { useAuth } from '../auth/useAuth';
import { HttpError } from '../api/client';

interface LocationState {
  from?: { pathname?: string };
}

const ENTRA_ERRORS: Record<string, string> = {
  entra_cancelled: 'Login cancelado.',
  entra_config_error: 'Entra ID nao configurado. Contate o administrador.',
  entra_invalid_response: 'Resposta invalida do Microsoft. Tente novamente.',
  entra_user_inactive: 'Sua conta esta inativa. Contate o administrador.',
  entra_auth_failed: 'Falha na autenticacao com Microsoft. Tente novamente.',
};

const LOCAL_LOGIN_ENABLED = import.meta.env.VITE_ENABLE_LOCAL_LOGIN === 'true';

export function LoginPage() {
  const { status, login, loginWithEntra } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [entraLoading, setEntraLoading] = useState(false);

  const urlError = new URLSearchParams(location.search).get('error');
  const [error, setError] = useState<string | null>(
    urlError ? (ENTRA_ERRORS[urlError] ?? `Erro de autenticacao: ${urlError}`) : null,
  );

  if (status === 'authenticated') {
    const from = (location.state as LocationState | null)?.from?.pathname ?? '/';
    return <Navigate to={from} replace />;
  }

  const onEntraLogin = () => {
    setEntraLoading(true);
    setError(null);
    void loginWithEntra();
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await login(email, password);
      const from = (location.state as LocationState | null)?.from?.pathname ?? '/';
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof HttpError) {
        setError(err.apiError.message);
      } else {
        setError((err as Error).message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <h1>Portal Prefect</h1>
          <p>Entre com sua conta para continuar</p>
        </div>

        <button
          type="button"
          className="btn btn-primary btn-block"
          disabled={entraLoading || submitting}
          onClick={() => void onEntraLogin()}
        >
          {entraLoading ? (
            <>
              <span className="btn-spinner" /> Aguardando Microsoft...
            </>
          ) : (
            <>
              <IconMicrosoft size={16} /> Entrar com Microsoft
            </>
          )}
        </button>

        {error ? <div className="form-error" style={{ marginTop: '0.75rem' }}>{error}</div> : null}

        {LOCAL_LOGIN_ENABLED && (
          <>
            <div className="divider"><span>ou</span></div>
            <form onSubmit={onSubmit} className="login-form">
              <label className="field">
                <span>Email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  required
                />
              </label>
              <label className="field">
                <span>Senha</span>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </label>
              <button type="submit" className="btn btn-secondary btn-block" disabled={submitting || entraLoading}>
                {submitting ? 'Entrando...' : 'Entrar'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
